import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiSearch, FiRefreshCw, FiBook } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import './Students.css';

const Students = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingRiwayat, setSyncingRiwayat] = useState(false);
  const [programList, setProgramList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    program: '',
    status: '',
    enrollmentYear: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({});
  const [viewModal, setViewModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const { user } = useAuth();

  const fetchProgramList = useCallback(async () => {
    try {
      // Get unique programs from students collection
      const res = await axios.get('/api/students/programs');
      setProgramList(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch program list:', error);
    }
  }, []);

  const fetchStatusList = useCallback(async () => {
    try {
      // Get unique status from students collection
      const res = await axios.get('/api/students/status-list');
      setStatusList(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch status list:', error);
    }
  }, []);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const res = await axios.get(`/api/students?${queryParams}`);
      setStudents(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      toast.error('Gagal mengambil data mahasiswa');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchStudents();
    fetchProgramList();
    fetchStatusList();
  }, [fetchStudents, fetchProgramList, fetchStatusList]);

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus mahasiswa ini?')) {
      try {
        await axios.delete(`/api/students/${id}`);
        toast.success('Mahasiswa berhasil dihapus');
        fetchStudents();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Gagal menghapus mahasiswa');
      }
    }
  };

  const handleViewStudent = async (id) => {
    try {
      // Fetch dengan cache buster untuk data terbaru
      const res = await axios.get(`/api/students/${id}?t=${new Date().getTime()}`);
      setSelectedStudent(res.data.data);
      setViewModal(true);
    } catch (error) {
      toast.error('Gagal mengambil detail mahasiswa');
    }
  };

  const handleCloseModal = () => {
    setViewModal(false);
    setSelectedStudent(null);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
      page: 1
    });
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleSyncFromAKM = async () => {
    if (!window.confirm('Sinkronisasi IPK dan status dari AKM terbaru?\nProses ini akan memperbarui data IPK dan status semua mahasiswa.')) {
      return;
    }

    setSyncing(true);
    try {
      const res = await axios.post('/api/students/sync-from-akm');
      toast.success(res.data.message || 'Sinkronisasi berhasil');
      fetchStudents(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal sinkronisasi data');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncRiwayatPendidikan = async () => {
    if (!window.confirm('Sinkronisasi riwayat pendidikan dari Neo Feeder?\nProses ini akan memperbarui riwayat pendidikan semua mahasiswa.')) {
      return;
    }

    setSyncingRiwayat(true);
    try {
      const res = await axios.post('/api/students/sync-riwayat-pendidikan');
      toast.success(res.data.message || 'Sinkronisasi riwayat pendidikan berhasil');
      if (res.data.data) {
        toast.info(`Berhasil: ${res.data.data.success}, Tidak ada data: ${res.data.data.noData}`);
      }
      fetchStudents(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal sinkronisasi riwayat pendidikan');
    } finally {
      setSyncingRiwayat(false);
    }
  };

  const getStatusBadge = (status) => {
    // Normalize status untuk case-insensitive comparison
    const normalizedStatus = status?.toLowerCase() || '';
    
    // Mapping badge berdasarkan keyword dalam nama status
    if (normalizedStatus.includes('aktif') && !normalizedStatus.includes('non')) {
      return 'table-badge-success';
    }
    if (normalizedStatus.includes('cuti')) {
      return 'table-badge-warning';
    }
    if (normalizedStatus.includes('lulus') || normalizedStatus.includes('alumni')) {
      return 'table-badge-info';
    }
    if (normalizedStatus.includes('non') || normalizedStatus.includes('keluar') || 
        normalizedStatus.includes('drop') || normalizedStatus.includes('mengundurkan')) {
      return 'table-badge-danger';
    }
    
    // Default
    return 'table-badge-secondary';
  };

  const canEdit = user?.role === 'admin' || user?.role === 'staff';
  const canDelete = user?.role === 'admin';

  return (
    <div className="students-page">
      {/* Modern Transparent Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiBook className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Data Mahasiswa</h1>
              <p className="header-subtitle">Kelola dan monitor data mahasiswa secara realtime</p>
            </div>
          </div>
          <div className="header-actions">
            {canEdit && (
              <>
                <button 
                  onClick={handleSyncFromAKM} 
                  className="btn btn-glass btn-info"
                  disabled={syncing}
                  title="Sinkronisasi IPK dari AKM"
                >
                  <FiRefreshCw className={syncing ? 'spinning' : ''} /> 
                  <span className="btn-text">{syncing ? 'Syncing...' : 'Sync IPK'}</span>
                </button>
                <button 
                  onClick={handleSyncRiwayatPendidikan} 
                  className="btn btn-glass btn-secondary"
                  disabled={syncingRiwayat}
                  title="Sinkronisasi Riwayat Pendidikan"
                >
                  <FiBook className={syncingRiwayat ? 'spinning' : ''} /> 
                  <span className="btn-text">{syncingRiwayat ? 'Syncing...' : 'Sync Riwayat'}</span>
                </button>
                <Link to="/students/new" className="btn btn-glass btn-primary">
                  <FiPlus /> 
                  <span className="btn-text">Tambah Mahasiswa</span>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card filters-card">
        <div className="filters-grid">
          <div className="form-group">
            <div className="search-box">
              <FiSearch />
              <input
                type="text"
                name="search"
                placeholder="Cari NIM, nama, atau email..."
                value={filters.search}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="form-group">
            <select
              name="program"
              value={filters.program}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="">Semua Program Studi</option>
              {programList.map((program, index) => (
                <option key={index} value={program}>
                  {program}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="">Semua Status</option>
              {statusList.map((status, index) => (
                <option key={index} value={status.nama}>
                  {status.nama} ({status.count})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <select
              name="enrollmentYear"
              value={filters.enrollmentYear || ''}
              onChange={handleFilterChange}
              className="form-control"
            >
              <option value="">Berdasarkan Tahun Masuk</option>
              {[2025,2024,2023,2022,2021,2020].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="spinner"></div>
        ) : students.length === 0 ? (
          <div className="empty-state">
            <p>Tidak ada data mahasiswa</p>
          </div>
        ) : (
          <>
            <div className="modern-table-container">
              <table className="modern-table accent-blue">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>NIM</th>
                    <th>Nama</th>
                    <th>Program Studi</th>
                    <th>Tahun Masuk</th>
                    <th>IPK</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, idx) => (
                    <tr key={student._id}>
                      <td>{(pagination.page - 1) * pagination.limit + idx + 1}</td>
                      <td>{student.nim}</td>
                      <td><strong>{student.name}</strong></td>
                      <td>{student.nama_program_studi || student.program || '-'}</td>
                      <td>{student.enrollmentYear || '-'}</td>
                      <td><strong>{student.gpa ? student.gpa.toFixed(2) : student.ipk || '-'}</strong></td>
                      <td>
                        <span className={`table-badge ${getStatusBadge(student.nama_status_mahasiswa || student.status)}`}>
                          {student.nama_status_mahasiswa || student.status || '-'}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => handleViewStudent(student._id)}
                            className="table-btn table-btn-view"
                            title="Lihat Detail"
                          >
                            <FiEye />
                          </button>
                          {canEdit && (
                            <Link
                              to={`/students/edit/${student._id}`}
                              className="table-btn table-btn-edit"
                              title="Edit"
                            >
                              <FiEdit />
                            </Link>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(student._id)}
                              className="table-btn table-btn-delete"
                              title="Hapus"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="table-pagination">
                <div className="table-pagination-info">
                  Halaman {pagination.page} dari {pagination.pages} ({pagination.total} data)
                </div>
                <div className="table-pagination-buttons">
                  <button
                    className="table-pagination-button"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <button
                    className="table-pagination-button"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal View Detail Mahasiswa */}
      {viewModal && selectedStudent && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div className="biodata-avatar">
                  <img 
                    src={selectedStudent.photo && selectedStudent.photo !== 'default-avatar.png' 
                      ? `/uploads/students/${selectedStudent.photo}` 
                      : '/default-avatar.png'
                    } 
                    alt="Avatar"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/default-avatar.png';
                    }}
                  />
                </div>
                <div>
                  <h3 style={{ margin: 0 }}>Detail Biodata Mahasiswa</h3>
                  <p style={{ margin: '5px 0 0 0', color: '#7f8c8d', fontSize: '14px' }}>
                    {selectedStudent.name} - {selectedStudent.nim}
                  </p>
                </div>
              </div>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="biodata-grid">
                {/* Data Pribadi */}
                <div className="biodata-section">
                  <h4 className="biodata-section-title">Data Pribadi</h4>
                  <div className="biodata-row">
                    <span className="biodata-label">NIM</span>
                    <span className="biodata-value">{selectedStudent.nim || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">NIPD</span>
                    <span className="biodata-value">{selectedStudent.nipd || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Nama Lengkap</span>
                    <span className="biodata-value"><strong>{selectedStudent.name || '-'}</strong></span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">NIK</span>
                    <span className="biodata-value">{selectedStudent.nik || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">NISN</span>
                    <span className="biodata-value">{selectedStudent.nisn || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Jenis Kelamin</span>
                    <span className="biodata-value">{selectedStudent.gender || selectedStudent.jenis_kelamin || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Tempat Lahir</span>
                    <span className="biodata-value">{selectedStudent.tempat_lahir || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Tanggal Lahir</span>
                    <span className="biodata-value">
                      {selectedStudent.dateOfBirth 
                        ? new Date(selectedStudent.dateOfBirth).toLocaleDateString('id-ID', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'
                          })
                        : '-'}
                    </span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Agama</span>
                    <span className="biodata-value">{selectedStudent.nama_agama || selectedStudent.agama || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Kewarganegaraan</span>
                    <span className="biodata-value">{selectedStudent.nama_kewarganegaraan || selectedStudent.kewarganegaraan || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">NPWP</span>
                    <span className="biodata-value">{selectedStudent.npwp || '-'}</span>
                  </div>
                </div>

                {/* Data Kontak */}
                <div className="biodata-section">
                  <h4 className="biodata-section-title">Data Kontak</h4>
                  <div className="biodata-row">
                    <span className="biodata-label">Email</span>
                    <span className="biodata-value">{selectedStudent.email || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Handphone</span>
                    <span className="biodata-value">{selectedStudent.handphone || selectedStudent.phone || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Telepon</span>
                    <span className="biodata-value">{selectedStudent.telepon || '-'}</span>
                  </div>
                </div>

                {/* Alamat */}
                <div className="biodata-section">
                  <h4 className="biodata-section-title">Alamat</h4>
                  <div className="biodata-row">
                    <span className="biodata-label">Jalan</span>
                    <span className="biodata-value">{selectedStudent.jalan || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">RT / RW</span>
                    <span className="biodata-value">
                      {selectedStudent.rt || '-'} / {selectedStudent.rw || '-'}
                    </span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Dusun</span>
                    <span className="biodata-value">{selectedStudent.dusun || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Kelurahan</span>
                    <span className="biodata-value">{selectedStudent.kelurahan || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Kode Pos</span>
                    <span className="biodata-value">{selectedStudent.kode_pos || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Wilayah</span>
                    <span className="biodata-value">{selectedStudent.nama_wilayah || '-'}</span>
                  </div>
                </div>

                {/* Status */}
                <div className="biodata-section">
                  <h4 className="biodata-section-title">Status Mahasiswa</h4>
                  <div className="biodata-row">
                    <span className="biodata-label">Status</span>
                    <span className="biodata-value">
                      <span className={`table-badge ${getStatusBadge(selectedStudent.nama_status_mahasiswa || selectedStudent.status)}`}>
                        {selectedStudent.nama_status_mahasiswa || selectedStudent.status || '-'}
                      </span>
                    </span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Penerima KPS</span>
                    <span className="biodata-value">
                      {selectedStudent.penerima_kps ? 'Ya' : selectedStudent.penerima_kps === false ? 'Tidak' : '-'}
                    </span>
                  </div>
                </div>

                {/* Data Orang Tua */}
                <div className="biodata-section">
                  <h4 className="biodata-section-title">Data Orang Tua</h4>
                  <div className="biodata-row">
                    <span className="biodata-label">Nama Ibu Kandung</span>
                    <span className="biodata-value">{selectedStudent.nama_ibu_kandung || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">NIK Ibu</span>
                    <span className="biodata-value">{selectedStudent.nik_ibu || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">Nama Ayah</span>
                    <span className="biodata-value">{selectedStudent.nama_ayah || '-'}</span>
                  </div>
                  <div className="biodata-row">
                    <span className="biodata-label">NIK Ayah</span>
                    <span className="biodata-value">{selectedStudent.nik_ayah || '-'}</span>
                  </div>
                </div>

                {/* Info Sinkronisasi */}
                {selectedStudent.last_sync && (
                  <div className="biodata-section">
                    <h4 className="biodata-section-title">Informasi Sinkronisasi</h4>
                    <div className="biodata-row">
                      <span className="biodata-label">Terakhir Disinkronkan</span>
                      <span className="biodata-value">
                        {new Date(selectedStudent.last_sync).toLocaleString('id-ID', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                )}

                {/* Riwayat Pendidikan */}
                {selectedStudent.riwayat_pendidikan && selectedStudent.riwayat_pendidikan.length > 0 && (
                  <div className="biodata-section" style={{ gridColumn: '1 / -1' }}>
                    <h4 className="biodata-section-title">Riwayat Pendidikan</h4>
                    {selectedStudent.riwayat_pendidikan.map((riwayat, index) => (
                      <div key={index} style={{ 
                        marginBottom: '20px', 
                        padding: '15px', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '8px',
                        backgroundColor: '#f9fafb'
                      }}>
                        <h5 style={{ marginBottom: '10px', color: '#1f2937', fontSize: '16px', fontWeight: '600' }}>
                          Riwayat #{index + 1}
                        </h5>
                        <div className="biodata-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
                          <div className="biodata-row">
                            <span className="biodata-label">Program Studi</span>
                            <span className="biodata-value"><strong>{riwayat.nama_program_studi || '-'}</strong></span>
                          </div>
                          <div className="biodata-row">
                            <span className="biodata-label">Angkatan</span>
                            <span className="biodata-value">{riwayat.angkatan || '-'}</span>
                          </div>
                          <div className="biodata-row">
                            <span className="biodata-label">Jenis Pendaftaran</span>
                            <span className="biodata-value">{riwayat.nama_jenis_daftar || '-'}</span>
                          </div>
                          <div className="biodata-row">
                            <span className="biodata-label">Jalur Masuk</span>
                            <span className="biodata-value">{riwayat.nama_jalur_masuk || '-'}</span>
                          </div>
                          <div className="biodata-row">
                            <span className="biodata-label">Tanggal Daftar</span>
                            <span className="biodata-value">
                              {riwayat.tanggal_daftar 
                                ? new Date(riwayat.tanggal_daftar).toLocaleDateString('id-ID', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric'
                                  })
                                : '-'}
                            </span>
                          </div>
                          {riwayat.id_jenis_keluar && (
                            <>
                              <div className="biodata-row">
                                <span className="biodata-label">Jenis Keluar</span>
                                <span className="biodata-value">{riwayat.nama_jenis_keluar || '-'}</span>
                              </div>
                              <div className="biodata-row">
                                <span className="biodata-label">Tanggal Keluar</span>
                                <span className="biodata-value">
                                  {riwayat.tanggal_keluar 
                                    ? new Date(riwayat.tanggal_keluar).toLocaleDateString('id-ID')
                                    : '-'}
                                </span>
                              </div>
                              <div className="biodata-row">
                                <span className="biodata-label">Keterangan Keluar</span>
                                <span className="biodata-value">{riwayat.keterangan_keluar || '-'}</span>
                              </div>
                            </>
                          )}
                          {riwayat.id_perguruan_tinggi_asal && (
                            <>
                              <div className="biodata-row">
                                <span className="biodata-label">PT Asal</span>
                                <span className="biodata-value">{riwayat.nama_perguruan_tinggi_asal || '-'}</span>
                              </div>
                              <div className="biodata-row">
                                <span className="biodata-label">Prodi Asal</span>
                                <span className="biodata-value">{riwayat.nama_prodi_asal || '-'}</span>
                              </div>
                              <div className="biodata-row">
                                <span className="biodata-label">SKS Diakui</span>
                                <span className="biodata-value">{riwayat.sks_diakui || '0'}</span>
                              </div>
                            </>
                          )}
                          {riwayat.biaya_masuk > 0 && (
                            <div className="biodata-row">
                              <span className="biodata-label">Biaya Masuk</span>
                              <span className="biodata-value">
                                Rp {new Intl.NumberFormat('id-ID').format(riwayat.biaya_masuk)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
